import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import RouteSummaryPathStore from "./store/routeSummaryPathStore";
import { useDropdownStore } from "../AssetVehicle/store/useDropdownStore";
import { useEffect } from "react";
import { AllDriverDetails } from "../../Apis/driverApi";
import { AllVehicleDetails } from "../../Apis/vehicleApi";
import { useState } from "react";
import Select from "react-select";

const EditRouteSummaryPathModal = ({ show, onHide, data, t }) => {
  const {
    fetchRouteSummaryPathMasters,
    updateRouteSummaryPathData,
    fetchDriverByVehicleId,
    driverData,
    clearDriverData,
  } = RouteSummaryPathStore();
  const [vehicleDetails, setVehicleDetails] = useState([]);
  const fetchVehicleData = async () => {
    try {
      const response = await AllVehicleDetails();
      setVehicleDetails(response);
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
    }
  };

  // Initial values from props data, with fallback to empty strings
  const initialValues = {
    routeCode: data?.routeCode || "",
    startingCollectionPointCode: data?.startingCollectionPointCode || "",
    startingPointName: data?.startingPointName || "",
    endingCollectionPointCode: data?.endingCollectionPointCode || "",
    routeSupervisorName: data?.routeSupervisorName || "",
    zoneName: data?.zoneName || "",
    circleName: data?.circleName || "",
    status:
      data && data.status !== undefined
        ? data.status === true || data.status === "Active"
          ? "Active"
          : "Inactive"
        : "Active", // Default to "Active" if no status is provided
    driverId: data?.driverId || data?.driverID || "", // Added driverId with fallback
    vehicleId: data?.vehicleId || data?.vehicleID || "", // Added vehicleId with fallback
  };

  // Simple validation schema that always validates driver and vehicle
  const simpleValidationSchema = Yup.object({
    startingCollectionPointCode: Yup.string().required(
      t("Starting Point Code is required")
    ),
    startingPointName: Yup.string().required(
      t("Starting Point Name is required")
    ),
    endingCollectionPointCode: Yup.string().required(
      t("Ending Point Code is required")
    ),
    routeSupervisorName: Yup.string().required(t("Route Incharge is required")),
    zoneName: Yup.string().required(t("Zone is required")),
    circleName: Yup.string().required(t("Ward is required")),
    status: Yup.string()
      .oneOf(["Active", "Inactive"], t("Status is required"))
      .required(t("Status is required")),
    driverId: Yup.string().required(t("Driver is required")),
    vehicleId: Yup.string().required(t("Vehicle is required")),
  });

  useEffect(() => {
    fetchVehicleData();
  }, []);

  // Effect to fetch drivers for selected vehicle when modal opens
  useEffect(() => {
    if (show && data?.vehicleId) {
      fetchDriverByVehicleId(data.vehicleId);
    }
  }, [show, data]);

  // Handle vehicle selection to fetch related drivers
  const handleVehicleChange = async (vehicleId, setFieldValue, validateField) => {
    // Clear driver field when vehicle is changed or removed
    setFieldValue("driverId", "");
    
    if (vehicleId) {
      try {
        await fetchDriverByVehicleId(vehicleId);
      } catch (error) {
        console.error("Error fetching drivers for vehicle:", error);
      }
    } else {
      // If no vehicle selected, clear driver data from store
      clearDriverData();
    }
    
    // Trigger validation for both vehicle and driver fields
    validateField("vehicleId");
    validateField("driverId");
  };

  // Handle driver selection
  const handleDriverChange = (driverId, setFieldValue, validateField) => {
    setFieldValue("driverId", driverId);
    validateField("driverId");
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const updatedValues = {
        status: values.status === "Active" ? true : false,
        driverId: values.driverId, // Include driverId in update
        vehicleId: values.vehicleId, // Include vehicleId in update
      };
      if (data && data.routeId) {
        const result = await updateRouteSummaryPathData({
          ...updatedValues,
          routeId: data.routeId,
        });

        if (result && result.success) {
          fetchRouteSummaryPathMasters(); // Refresh data after update
          console.log("Update successful:", {
            routeId: data.routeId,
            ...updatedValues,
          });
          onHide(); // Only close modal on success
        } else {
          console.error("Update failed: API returned error");
        }
      } else {
        console.error("routeId is missing from data");
      }
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header>
        <Modal.Title>{t("Edit Route")}</Modal.Title>
        <i className="fa-solid fa-xmark close-container" onClick={onHide}></i>
      </Modal.Header>
      <hr />
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={simpleValidationSchema}
          onSubmit={handleSubmit}
          validateOnMount={true}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            handleSubmit,
            isSubmitting,
            touched,
            errors,
            setFieldValue,
            validateField,
            values,
          }) => {
            return (
              <Form noValidate onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="formRouteCode">
                      <Form.Label>{t("Route Code")}</Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="routeCode"
                        placeholder={t("Enter")}
                        disabled
                        isInvalid={touched.routeCode && !!errors.routeCode}
                      />
                      <ErrorMessage
                        name="routeCode"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group
                      className="mb-3"
                      controlId="formStartingPointCode"
                    >
                      <Form.Label>{t("Starting Point Code")}</Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="startingCollectionPointCode"
                        placeholder={t("Enter")}
                        disabled
                        isInvalid={
                          touched.startingCollectionPointCode &&
                          !!errors.startingCollectionPointCode
                        }
                      />
                      <ErrorMessage
                        name="startingCollectionPointCode"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group
                      className="mb-3"
                      controlId="formStartingPointName"
                    >
                      <Form.Label>{t("Starting Point Name")}</Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="startingPointName"
                        placeholder={t("Enter")}
                        disabled
                        isInvalid={
                          touched.startingPointName &&
                          !!errors.startingPointName
                        }
                      />
                      <ErrorMessage
                        name="startingPointName"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group
                      className="mb-3"
                      controlId="formEndingPointCode"
                    >
                      <Form.Label>{t("Ending Point Code")}</Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="endingCollectionPointCode"
                        placeholder={t("Enter")}
                        disabled
                        isInvalid={
                          touched.endingCollectionPointCode &&
                          !!errors.endingCollectionPointCode
                        }
                      />
                      <ErrorMessage
                        name="endingCollectionPointCode"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group
                      className="mb-3"
                      controlId="formRouteSupervisorName"
                    >
                      <Form.Label>
                        {t("Created By (Route Incharge)")}
                      </Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="routeSupervisorName"
                        placeholder={t("Enter")}
                        disabled
                        isInvalid={
                          touched.routeSupervisorName &&
                          !!errors.routeSupervisorName
                        }
                      />
                      <ErrorMessage
                        name="routeSupervisorName"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="formZoneName">
                      <Form.Label>{t("Zone")}</Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="zoneName"
                        placeholder={t("Enter")}
                        disabled
                        isInvalid={touched.zoneName && !!errors.zoneName}
                      />
                      <ErrorMessage
                        name="zoneName"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="formCircleName">
                      <Form.Label>{t("Ward")}</Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="circleName"
                        placeholder={t("Enter")}
                        disabled
                        isInvalid={touched.circleName && !!errors.circleName}
                      />
                      <ErrorMessage
                        name="circleName"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="formVehicleId">
                      <Form.Label>{t("Vehicle")} <span className="text-danger">*</span></Form.Label>
                      <Field name="vehicleId">
                        {({ field, form }) => (
                          <Select
                            {...field}
                            classNamePrefix="CreatableSelect"
                            options={vehicleDetails
                              ?.filter((vehicle) => vehicle.isActive && vehicle.collectionTypeId === 2)
                              .map((vehicle) => ({
                                value: vehicle.vehicleId,
                                label: vehicle.vehicleNumber,
                              }))}
                            // isDisabled={
                            //   data?.hasVehicle ||
                            //   (data?.vehicleId && data?.vehicleId !== "")
                            // }
                            isSearchable={true}
                            isClearable={true}
                            placeholder="Select Vehicle"
                            onChange={(selectedOption) => {
                              const vehicleId = selectedOption?.value || "";
                              form.setFieldValue("vehicleId", vehicleId);
                              handleVehicleChange(
                                vehicleId,
                                form.setFieldValue,
                                form.validateField
                              );
                            }}
                            value={vehicleDetails
                              ?.filter((vehicle) => vehicle.isActive && vehicle.collectionTypeId === 2)
                              .map((vehicle) => ({
                                value: vehicle.vehicleId,
                                label: vehicle.vehicleNumber,
                              }))
                              .find((option) => option.value === field.value) || null}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="vehicleId"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="formDriverId">
                      <Form.Label>{t("Driver")} <span className="text-danger">*</span></Form.Label>
                      <Field name="driverId">
                        {({ field, form }) => (
                          <Select
                            {...field}
                            classNamePrefix="CreatableSelect"
                            options={driverData?.map((driver) => ({
                              value: driver.driverId,
                              label: driver.driverName,
                            }))}
                            // isDisabled={
                            //   data?.hasDriver ||
                            //   (data?.driverId && data?.driverId !== "")
                            // }
                            isSearchable={true}
                            isClearable={true}
                            placeholder="Select Driver"
                            onChange={(selectedOption) => {
                              const driverId = selectedOption?.value || "";
                              form.setFieldValue("driverId", driverId);
                              handleDriverChange(
                                driverId,
                                form.setFieldValue,
                                form.validateField
                              );
                            }}
                            value={driverData
                              ?.map((driver) => ({
                                value: driver.driverId,
                                label: driver.driverName,
                              }))
                              .find((option) => option.value === field.value) || null}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="driverId"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="formStatus">
                      <Form.Label>{t("Status")}</Form.Label>
                      <div className="d-flex align-items-center">
                        <label className="me-4">
                          <Field type="radio" name="status" value="Active" />{" "}
                          {t("Active")}
                        </label>
                        <label>
                          <Field type="radio" name="status" value="Inactive" />{" "}
                          {t("In-active")}
                        </label>
                      </div>
                      <ErrorMessage
                        name="status"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="modal-footer d-flex justify-content-center">
                  <button
                    type="submit"
                    className="deployment-button"
                    disabled={isSubmitting}
                  >
                    <i className="fa-solid fa-check done-container"></i>
                    Update
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default EditRouteSummaryPathModal;
